// Psychologist ("Психолог") — rebuilt to match the original psychologist-section:
// hero → report tile → "how it works" 5-step route → callout → gift offer.
// (The earlier version was a simplified 3-card placeholder.)
//
// - "Выгрузить отчёт" opens the report flow.
// - "Записаться на сессию" is gated (export_allowed): eligible → open Chatwoot
//   with a pre-message; otherwise relabel + toast.

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useChatwoot } from '../../hooks/useChatwoot'
import { useProfileStore } from '../../store/profileStore'
import { useChatStore } from '../../store/chatStore'
import { chatwootApi } from '../../api/chatwoot'
import { toast } from '../../store/toastStore'
import * as metrica from '../../lib/metrica'
import ReportFlowModal from './ReportFlowModal'
import SessionPopup from './SessionPopup'
import { downloadReportPdf } from '../../api/report'
import styles from './Psychologist.module.css'

const ROUTE = [
  { n: 1, title: 'Общение с ИИ', text: 'Работаете с AI-психологом над своей ситуацией в чате.' },
  { n: 2, title: 'Запрос сессии', text: '30 минут с живым психологом — с учётом прогресса.' },
  { n: 3, title: 'ИИ готовит отчёт', text: 'Задаёте вопрос для сессии и убираете лишнее.' },
  { n: 4, title: 'Сессия 30 мин', text: 'Ориентация, психоэдукация, донастройка бота.' },
  { n: 5, title: 'Выход из сессии', text: 'Настройки психолога — в память бота.' },
]

interface GiftPopup {
  badge: boolean
  text: string
  cta: string
}

const GIFT_POPUP: GiftPopup = {
  badge: true,
  text: 'Передайте отчёт психологу — он изучит вашу историю до встречи, и вы начнёте сразу с сути.',
  cta: 'Записаться на сессию',
}

export default function Psychologist() {
  const navigate = useNavigate()
  const { open: openChat } = useChatwoot()
  const loadStats = useProfileStore((s) => s.loadStats)

  const [sessionBlocked, setSessionBlocked] = useState(false)
  const [reportOpen, setReportOpen] = useState(false)
  const [popup, setPopup] = useState<GiftPopup | null>(null)

  // Arriving here right after a paid-session purchase → auto-open the widget.
  useEffect(() => {
    if (useChatStore.getState().consumeChatwootOpen()) openChat()
  }, [openChat])

  const openSessionChat = async () => {
    metrica.reachGoal('psychologist_session_chat')
    try {
      await chatwootApi.sendMessage({ message: 'Здравствуйте! Хочу записаться на подарочную консультацию' })
    } catch {
      /* widget opens regardless */
    }
    openChat()
  }

  const onSession = async () => {
    const stats = await loadStats()
    if (stats?.export_allowed) {
      openSessionChat()
    } else {
      metrica.reachGoal('psychologist_session_blocked')
      setSessionBlocked(true)
      toast('Запись на сессию доступна только на подписках с автопродлением', 'error')
    }
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.hero}>
        <div className={styles.eyebrow}>AI + живой психолог</div>
        <h2>Сессия с психологом с учётом вашего прогресса</h2>
        <p className={styles.lead}>
          Краткая консультация, чтобы сориентироваться и сделать один шаг. Работа с ботом и отчёт
          помогают сформулировать запрос к психологу — вы экономите время на прояснении ситуации.
        </p>
      </div>

      <div className={styles.reportTile}>
        <div className={styles.reportIcon}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="22" height="22">
            <path d="M14 3v4a1 1 0 0 0 1 1h4M5 3h9l5 5v11a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" />
            <path d="M8 13h8M8 17h5M8 9h2" />
          </svg>
        </div>
        <div className={styles.reportText}>
          <h4>Отчёт для подготовки к сессии</h4>
          <p>2 страницы прогресса работы с ботом. Доступно раз в 3 дня на тарифах с автопродлениями.</p>
        </div>
        <button className={styles.reportBtn} onClick={() => setReportOpen(true)}>Выгрузить отчёт</button>
      </div>

      <div className={styles.routeCard}>
        <p className={styles.routeTitle}>Как это работает</p>
        <div className={styles.route}>
          <div className={styles.routeLine}><span className={styles.routeRunner} /></div>
          {ROUTE.map((s) => (
            <div key={s.n} className={styles.rstep}>
              <div className={styles.node}>{s.n}</div>
              <div className={styles.rtext}>
                <h4>{s.title}</h4>
                <p>{s.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className={styles.callout}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 9v4m0 4h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
        </svg>
        <div>
          <h4>Это не регулярная терапия</h4>
          <p>
            Мы не ведём длительную терапию и не рекомендуем конкретных людей или сервисов. Но поможем
            понять, какой профиль специалиста вам нужен, — это маршрутизация.
          </p>
        </div>
      </div>

      <div className={styles.offer}>
        <span className={styles.offerBadge}>🎁 В подарок</span>
        <h3>Сессия с психологом-консультантом</h3>
        <div className={styles.price}>В подарок</div>
        <p>
          Дарим консультацию психолога пользователям платных тарифов — сделайте отчёт по работе с
          ботом и составьте план работы с опытным психологом.
        </p>
        <button
          className={`${styles.offerBtn} ${sessionBlocked ? styles.blocked : ''}`}
          onClick={onSession}
        >
          {sessionBlocked ? 'Доступно на тарифе с автопродлением' : 'Записаться на сессию'}
        </button>
        <p className={styles.offerNote}>Доступно на платных тарифах.</p>
      </div>

      {reportOpen && (
        <ReportFlowModal
          onClose={() => setReportOpen(false)}
          onShowExisting={() => {
            setReportOpen(false)
            navigate('/report')
          }}
          onCompleted={() => {
            setReportOpen(false)
            navigate('/report')
            metrica.reachGoal('psychologist_modal_open')
            setPopup(GIFT_POPUP)
          }}
        />
      )}

      {popup && (
        <SessionPopup
          {...popup}
          onBook={() => {
            setPopup(null)
            openSessionChat()
          }}
          onPdf={() => downloadReportPdf().catch(() => toast('Не удалось скачать PDF', 'error'))}
          onClose={() => setPopup(null)}
        />
      )}
    </div>
  )
}
